import { useCallback, useEffect, useMemo, useState } from 'react';
// import { Alert, Keyboard, Platform } from 'react-native';
// import { useLocalSearchParams, useRouter } from 'expo-router';
// import * as ExpoImagePicker from 'expo-image-picker';
// import * as ImageManipulator from 'expo-image-manipulator';
import { useNavigate, useParams, useLocation } from 'react-router-dom'

import { useCreateCommentMutation } from '@cobu/api-client';
import { CommentInput, CommentType, GroupComment } from '@cobu/types';
// import { useAppSelector } from '../store/hooks';
import { Strings } from '../constants';
import { isMarketplaceContent, findMarketplaceGroupId } from '../constants';
import { getModerationAlertConfig } from '../constants/moderation';
import { URLS } from '../constants';
import { useGroupsWithState } from './groups/groupsHooks';
import { UserInfo } from '@cobu/types';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
// import { showErrorMessage } from '@/utils/flashMessage';
import { analyticsService, AnalyticsEvent } from '@cobu/analytics';
import { logger } from '../utils/logger';
import { requestNativePermission } from '../lib/session/nativeMessaging';
import { updateNativePermission } from '../store/authSlice';
import { fixImageOrientation } from '../utils/fixImageOrientation';

type LocalParams = {
  groupId?: string;
  groupEventId?: string;
  replyingTo?: string; // JSON stringified GroupComment from caller
  enableMarketplaceModerator?: string; // 'true' | 'false'
  commentType?: string; // serialized enum from router params
  buildingId?: string;
  eventDate?: string;
};

type SelectedImage = { uri: string; mime?: string; file?: File };

export type UseComposeCommentResult = {
  user: UserInfo | null;
  replyingTo?: GroupComment;
  showReplyingTo: boolean;
  contents: string;
  setContents: (value: string) => void;
  images: SelectedImage[];
  removeImageAt: (index: number) => void;
  pickImage: () => Promise<void>;
  takePhoto: () => Promise<void>;
  isSubmitting: boolean;
  handlePost: () => Promise<void>;
  keyboardShown: number;
  userDisplayName: string | undefined;
  flaggedTerms: Array<{
    ruleId: string | number;
    match: string;
    start: number;
    end: number;
  }>;
  moderationAlert: {
    open: boolean;
    type: 'PROFANITY' | 'SENTIMENT' | null;
    confidence?: number;
  };
  showModerationAlert: (type: 'PROFANITY' | 'SENTIMENT', confidence?: number) => void;
  closeModerationAlert: () => void;
  handleModerationDecision: (decision: string) => Promise<void>;
  marketplaceAlert: {
    open: boolean;
  };
  showMarketplaceAlert: () => void;
  closeMarketplaceAlert: () => void;
  handleMarketplaceDecision: (decision: string) => Promise<void>;
};

const strings = Strings.get();
export function useComposeComment(): UseComposeCommentResult {
  const params = useParams<LocalParams>();
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const navigate = useNavigate();

  const groupId = params.groupId || (search.get('groupId') || undefined);
  const groupEventId = params.groupEventId || (search.get('groupEventId') || undefined);
  const buildingId = params.buildingId || (search.get('buildingId') || undefined);
  const eventDate = params.eventDate || (search.get('eventDate') || undefined);
  const enableMarketplaceModerator = (params.enableMarketplaceModerator as string) === 'true' || search.get('enableMarketplaceModerator') === 'true';
  const searchCommentType = search.get('commentType');
  const routeCommentType: CommentType =
    searchCommentType === 'GroupEventComment' || params.commentType === 'GroupEventComment' || !!groupEventId
      ? CommentType.GroupEventComment
      : CommentType.GroupComment;

  const replyingTo: GroupComment | undefined = useMemo(() => {
    const raw = params.replyingTo || search.get('replyingTo');
    if (!raw) return undefined;
    try {
      return JSON.parse(raw) as GroupComment;
    } catch (e) {
      return undefined;
    }
  }, [params.replyingTo, location.search]);

  const dispatch = useDispatch();
  const { user, nativePermissions, nativePermissionsProvided } = useSelector(
    (state: RootState) => state.auth
  );
  const { groups } = useGroupsWithState();

  const [contents, setContents] = useState<string>('');
  const [images, setImages] = useState<Array<SelectedImage>>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [keyboardShown, setKeyboardShown] = useState<number>(0);
  const [sessionReport, setSessionReport] = useState<string>('REPORT:');
  const [flaggedTerms, setFlaggedTerms] = useState<Array<{
    ruleId: string | number;
    match: string;
    start: number;
    end: number;
  }>>([]);
  const [moderationAlert, setModerationAlert] = useState<{
    open: boolean;
    type: 'PROFANITY' | 'SENTIMENT' | null;
    confidence?: number;
  }>({
    open: false,
    type: null,
  });
  const [marketplaceAlert, setMarketplaceAlert] = useState<{
    open: boolean;
  }>({
    open: false,
  });
  const MAX_IMAGES = 5;

  const createCommentMutation = useCreateCommentMutation(
    routeCommentType,
    groupId,
    groupEventId,
    buildingId,
    eventDate
  );

//   useEffect(() => {
//     if (Platform.OS === 'ios' || Platform.OS === 'android') {
//       keyboardDidShowListener.current = Keyboard.addListener('keyboardDidShow', e =>
//         setKeyboardShown(e.endCoordinates.height)
//       );
//       keyboardDidHideListener.current = Keyboard.addListener('keyboardDidHide', () =>
//         setKeyboardShown(0)
//       );
//     }
//     return () => {
//       keyboardDidShowListener.current?.remove?.();
//       keyboardDidHideListener.current?.remove?.();
//     };
//   }, []);

  const checkAndRequestPermission = useCallback(
    async (permission: 'camera' | 'library' | 'calendar', context?: string): Promise<boolean> => {
      if (!nativePermissionsProvided) {
        return true;
      }
      if (nativePermissions[permission]) {
        return true;
      }

      // Bypass "Go to Settings" prompt — request permission directly on the original click
      try {
        const granted = await requestNativePermission(permission, context);
        dispatch(updateNativePermission({ permission, granted }));
        return granted;
      } catch (_) {
        return false;
      }
    },
    [nativePermissions, nativePermissionsProvided, dispatch]
  );

  const submitComment = useCallback(
    async (extra?: Partial<CommentInput>) => {
      const input: CommentInput = {
        contents: contents.trim(),
        parent: replyingTo?.uuid,
        images: images as any[],
        groupId,
        groupEventId,
        editingReport: sessionReport,
        eventDate,
        ...(extra || {}),
      };
      const response = await createCommentMutation.mutateAsync(input);

      if (buildingId && !groupId && !groupEventId) {
        analyticsService.trackEvent(AnalyticsEvent.CREATE_CHAT_COMMENT);
      }

      if (extra?.userDecision === 'SEND_ANYWAY') {
        setFlaggedTerms([]);
        navigate(-1);
        return;
      }

      // Comment moderation alert flow
      if (response.moderationType) {
        setFlaggedTerms(response.flaggedTerms || []);
        if (response.moderationType === 'PROFANITY') {
          setSessionReport(prev =>
            `${prev}\n#####\n${contents}\n#####\n[Profanity: true, Confidence: ${response.confidence}]`
          );
          analyticsService.trackEvent(AnalyticsEvent.BLOCKED_PROFANITY, {
            email_address: user?.email,
            first_name: user?.firstName,
            last_name: user?.lastName,
            building_name: user?.building?.name,
            profanity_detected: contents,
          });
        } else if (response.moderationType === 'SENTIMENT') {
          setSessionReport(prev =>
            `${prev}\n#####\n${contents}\n#####\n[Sentiment: negative, Confidence: ${response.confidence}]`
          );
        }
        setModerationAlert({
          open: true,
          type: response.moderationType,
          confidence: response.confidence,
        });
        setIsSubmitting(false);
      } else {
        setFlaggedTerms([]);
        navigate(-1);
      }
    },
    [contents, replyingTo?.uuid, images, groupId, groupEventId, createCommentMutation, sessionReport, navigate, buildingId, eventDate, user]
  );

  const handleSetContents = useCallback((value: string) => {
    setContents(value);
    if (flaggedTerms.length > 0) {
      setFlaggedTerms([]);
    }
  }, [flaggedTerms.length]);

  const showMarketplaceAlert = useCallback(() => {
    setMarketplaceAlert({
      open: true,
    });
    setIsSubmitting(false);
  }, []);

  const handlePost = useCallback(async () => {
    if (!contents.trim() && images.length === 0) return;
    setIsSubmitting(true);
    try {
      // Marketplace detection for top-level comments
      if (enableMarketplaceModerator && !replyingTo?.uuid && isMarketplaceContent(contents)) {
        showMarketplaceAlert();
        return;
      }
      await submitComment();
    } catch (e) {
      // error feedback handled inside hooks
    } finally {
      setIsSubmitting(false);
    }
  }, [
    contents,
    images,
    enableMarketplaceModerator,
    replyingTo?.uuid,
    groups,
    groupId,
    createCommentMutation,
    strings,
    submitComment,
    navigate,
    showMarketplaceAlert,
  ]);

  const closeMarketplaceAlert = useCallback(() => {
    setMarketplaceAlert({
      open: false,
    });
  }, []);

  const handleMarketplaceDecision = useCallback(
    async (decision: string) => {
      const marketplaceGroupId = findMarketplaceGroupId(groups);
      
      if (decision === strings.discussion.marketplace.confirmYes) {
        try {
          await createCommentMutation.mutateAsync({
            contents: contents.trim(),
            images: images as any[],
            groupId: marketplaceGroupId || groupId,
            eventDate,
          });
          closeMarketplaceAlert();
          navigate(-1);
        } catch (e) {
          // handled by hooks
        } finally {
          setIsSubmitting(false);
        }
      } else if (decision === strings.discussion.marketplace.confirmNo) {
        try {
          await submitComment();
        } catch (e) {
          // handled by hooks
        } finally {
          setIsSubmitting(false);
          closeMarketplaceAlert();
        }
      }
    },
    [groups, contents, images, groupId, eventDate, createCommentMutation, submitComment, navigate, strings, closeMarketplaceAlert]
  );

  // Handle actions chosen in moderation alert
//   const handleModerationDecision = useCallback(
//     async (decision: string, confidence?: number) => {
//       switch (decision) {
//         case 'Send Anyway':
//           try {
//             const updated = `${sessionReport}\n#####\n${contents}`;
//             setSessionReport(updated);
//             await submitComment({ userDecision: 'SEND_ANYWAY', editingReport: updated });
//           } catch (_) {
//             // handled by hooks
//           } finally {
//             setIsSubmitting(false);
//           }
//           break;
//         case 'Edit Post':
//           setIsSubmitting(false);
//           break;
//         case 'View Code of Conduct':
//           router.push({
//             pathname: '/webview',
//             params: { url: URLS.CODE_OF_CONDUCT, title: strings.communityPledge.codeOfConduct.title },
//           });
//           setIsSubmitting(false);
//           break;
//       }
//     },
//     [router, submitComment]
//   );

//   const pickImage = useCallback(async () => {
//     try {
//       analyticsService.trackEvent(AnalyticsEvent.PRESS_COMMENT_IMAGE_UPLOAD_BUTTON);
//       if (images.length >= MAX_IMAGES) {
//         showErrorMessage(
//           strings.discussion.compose.imageLimit.replace('{max}', String(MAX_IMAGES))
//         );
//         return;
//       }
//       const { status: libStatus } = await ExpoImagePicker.requestMediaLibraryPermissionsAsync();
//       if (libStatus !== ExpoImagePicker.PermissionStatus.GRANTED) {
//         showErrorMessage(
//           strings.ui.imagePicker.permissionRequired,
//           strings.introduction.errors.photoLibraryPermissionDenied
//         );
//         return;
//       }
//       const remainingImagesCount = Math.max(0, MAX_IMAGES - images.length);
//       const result = await ExpoImagePicker.launchImageLibraryAsync({
//         mediaTypes: ExpoImagePicker.MediaTypeOptions.Images,
//         allowsEditing: false,
//         allowsMultipleSelection: true,
//         quality: 0.8,
//         base64: false,
//         selectionLimit: remainingImagesCount,
//       });
//       if (!result.canceled && result.assets && result.assets.length > 0) {
//         if (remainingImagesCount <= 0) {
//           showErrorMessage(
//             strings.discussion.compose.imageLimit.replace('{max}', String(MAX_IMAGES))
//           );
//           return;
//         }

//         const selected = result.assets.slice(0, remainingImagesCount);
//         const compressed = await Promise.all(
//           selected.map(async a => {
//             const origW = typeof a.width === 'number' ? a.width : undefined;
//             const origH = typeof a.height === 'number' ? a.height : undefined;

//             let actions: ImageManipulator.Action[] = [];
//             if (origW && origH) {
//               const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(origW, origH));
//               if (scale < 1) {
//                 actions = [{ resize: { width: Math.round(origW * scale), height: Math.round(origH * scale) } }];
//               }
//             }

//             const out = await ImageManipulator.manipulateAsync(
//               a.uri,
//               actions,
//               { compress: 0.8, format: ImageManipulator.SaveFormat.JPEG }
//             );
//             return { uri: out.uri, mime: 'image/jpeg' } as SelectedImage;
//           })
//         );

//         if (result.assets.length > remainingImagesCount) {
//           showErrorMessage(
//             strings.discussion.compose.imageLimit.replace('{max}', String(MAX_IMAGES))
//           );
//         }

//         setImages(prev => [...prev, ...compressed]);
//       }
//     } catch (e) {
//       showErrorMessage(strings.common.error.title, strings.ui.imagePicker.errors.pickImageFailed);
//     }
//   }, [strings, images]);

//   const takePhoto = useCallback(async () => {
//     try {
//       analyticsService.trackEvent(AnalyticsEvent.PRESS_COMMENT_IMAGE_UPLOAD_BUTTON);
//       if (images.length >= MAX_IMAGES) {
//         showErrorMessage(
//           strings.discussion.compose.imageLimit.replace('{max}', String(MAX_IMAGES))
//         );
//         return;
//       }
//       const { status: camStatus } = await ExpoImagePicker.requestCameraPermissionsAsync();
//       if (camStatus !== ExpoImagePicker.PermissionStatus.GRANTED) {
//         showErrorMessage(
//           strings.ui.imagePicker.permissionRequired,
//           strings.introduction.errors.cameraPermissionDenied
//         );
//         return;
//       }
//       const result = await ExpoImagePicker.launchCameraAsync({
//         allowsEditing: true,
//         quality: 0.8,
//         base64: false,
//       });
//       if (!result.canceled && result.assets && result.assets[0]) {
//         const asset = result.assets[0];
//         const origW = typeof asset.width === 'number' ? asset.width : undefined;
//         const origH = typeof asset.height === 'number' ? asset.height : undefined;
//         let actions: ImageManipulator.Action[] = [];
//         if (origW && origH) {
//           const scale = Math.min(1, MAX_IMAGE_DIMENSION / Math.max(origW, origH));
//           if (scale < 1) {
//             actions = [{ resize: { width: Math.round(origW * scale), height: Math.round(origH * scale) } }];
//           }
//         }

//         const out = await ImageManipulator.manipulateAsync(
//           asset.uri,
//           actions,
//           { compress: 0.8, format: ImageManipulator.SaveFormat.JPEG }
//         );

//         setImages(prev => {
//           if (prev.length >= MAX_IMAGES) {
//             showErrorMessage(
//               strings.discussion.compose.imageLimit.replace('{max}', String(MAX_IMAGES))
//             );
//             return prev;
//           }
//           return [...prev, { uri: out.uri, mime: 'image/jpeg' }];
//         });
//       }
//     } catch (e) {
//       showErrorMessage(strings.common.error.title, strings.ui.imagePicker.errors.takePictureFailed);
//     }
//   }, [strings, images]);

  const removeImageAt = useCallback((index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  }, []);

  const pickImageInternal = useCallback(async () => {
    try {
      if (images.length >= MAX_IMAGES) return;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      input.multiple = true;
      // Append to DOM so iOS WKWebView honors the click
      input.style.display = 'none';
      input.style.position = 'absolute';
      document.body.appendChild(input);
      input.onchange = async () => {
        const files = Array.from(input.files || []);
        const remaining = Math.max(0, MAX_IMAGES - images.length);
        const selected = files.slice(0, remaining);

        try {
          // Normalize orientation for each selected image
          const correctedFiles = await Promise.all(
            selected.map((file) => fixImageOrientation(file))
          );

          const mapped = correctedFiles.map(
            (file) =>
              ({
                uri: URL.createObjectURL(file),
                mime: file.type,
                file,
              } as SelectedImage)
          );

          setImages((prev) => [...prev, ...mapped]);
        } finally {
          input.remove();
        }
      };
      // Reset so choosing the same file twice still fires change
      input.value = '';
      input.click();
    } catch (e) {
      // no-op for web
    }
  }, [images.length]);

  const pickImage = useCallback(async () => {
    try {
      const hasPermission = await checkAndRequestPermission('library', 'compose_comment_gallery');
      if (!hasPermission) {
        return;
      }
      await pickImageInternal();
    } catch (e) {
      // no-op for web
    }
  }, [checkAndRequestPermission, pickImageInternal]);

  const takePhotoInternal = useCallback(async () => {
    try {
      if (images.length >= MAX_IMAGES) return;
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/*';
      // @ts-ignore - capture is valid on mobile browsers
      input.capture = 'environment';
      input.style.display = 'none';
      input.style.position = 'absolute';
      document.body.appendChild(input);
      input.onchange = async () => {
        const files = Array.from(input.files || []);
        if (files.length === 0) {
          input.remove();
          return;
        }
        const file = files[0];

        try {
          const correctedFile = await fixImageOrientation(file);
          const mapped = {
            uri: URL.createObjectURL(correctedFile),
            mime: correctedFile.type,
            file: correctedFile,
          } as SelectedImage;

          setImages((prev) =>
            prev.length >= MAX_IMAGES ? prev : [...prev, mapped]
          );
        } finally {
          input.remove();
        }
      };
      input.value = '';
      input.click();
    } catch (e) {
      // no-op for web
    }
  }, [images.length]);

  const takePhoto = useCallback(async () => {
    try {
      const hasPermission = await checkAndRequestPermission('camera', 'compose_comment_camera');
      if (!hasPermission) {
        return;
      }
      await takePhotoInternal();
    } catch (e) {
      // no-op for web
    }
  }, [checkAndRequestPermission, takePhotoInternal]);

  const showReplyingTo = replyingTo && replyingTo.type !== 'NEW_USERS';

  const showModerationAlert = useCallback(
    (moderationType: 'PROFANITY' | 'SENTIMENT', confidence?: number) => {
      setModerationAlert({
        open: true,
        type: moderationType,
        confidence,
      });
    },
    []
  );

  const closeModerationAlert = useCallback(() => {
    setModerationAlert({
      open: false,
      type: null,
    });
  }, []);

  // Handle actions chosen in moderation alert
  const handleModerationDecision = useCallback(
    async (decision: string) => {
      const moderationType = moderationAlert.type;
      const confidence = moderationAlert.confidence;
      
      switch (decision) {
        case strings.discussion.moderationAlerts.sentiment.options.sendAnyway:
          setIsSubmitting(true);
          try {
            const updated = `${sessionReport}\n#####\n${contents}`;
            setSessionReport(updated);
            await submitComment({ userDecision: 'SEND_ANYWAY', editingReport: updated });
          } catch (_) {
            // handled by hooks
          } finally {
            setIsSubmitting(false);
            closeModerationAlert();
          }
          break;
        case strings.discussion.moderationAlerts.profanity.options.editPost:
        case strings.discussion.moderationAlerts.sentiment.options.editPost:
          setIsSubmitting(false);
          closeModerationAlert();
          break;
        case strings.discussion.moderationAlerts.profanity.options.viewCodeOfConduct:
        case strings.discussion.moderationAlerts.sentiment.options.viewCodeOfConduct:
          setIsSubmitting(false);
          closeModerationAlert();
          if (URLS.CODE_OF_CONDUCT) {
            // Open in new window since Intercom doesn't allow iframes
            window.open(URLS.CODE_OF_CONDUCT, '_blank', 'noopener,noreferrer');
          } else {
            logger.error('Missing CODE_OF_CONDUCT URL');
          }
          break;
      }
    },
    [moderationAlert.type, moderationAlert.confidence, sessionReport, contents, submitComment, navigate, strings, closeModerationAlert]
  );

  return {
    user,
    replyingTo,
    showReplyingTo: Boolean(showReplyingTo),
    contents,
    setContents: handleSetContents,
    images,
    removeImageAt,
    pickImage,
    takePhoto,
    isSubmitting,
    handlePost,
    keyboardShown,
    userDisplayName: user?.displayName,
    flaggedTerms,
    moderationAlert,
    showModerationAlert,
    closeModerationAlert,
    handleModerationDecision,
    marketplaceAlert,
    showMarketplaceAlert,
    closeMarketplaceAlert,
    handleMarketplaceDecision,
  } as unknown as UseComposeCommentResult;
}

export default useComposeComment;
